// src/views/EditWarehouseFees.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync } from 'react-icons/fa';
import './Invoices.css';

const API = import.meta.env.VITE_API_URL;

export default function EditWarehouseFees() {
  const [rows, setRows] = useState([]); // { id, whstorageunit, fee, name }
  const [units, setUnits] = useState([]); // {id, name}
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [uRes, rRes] = await Promise.all([
          fetch(`${API}/api/packagingunits/storageunits`),
          fetch(`${API}/api/templates/warehousefees`),
        ]);
        const u = await uRes.json();
        const r = await rRes.json();
        setUnits(Array.isArray(u) ? u : []);
        setRows(Array.isArray(r) ? r : []);
      } catch {
        setUnits([]); setRows([]);
      }
    })();
  }, []);

  const unitName = (id) => (units.find(u => Number(u.id) === Number(id))?.name || '');
  const unitPickPriority = (id) => {
    const u = units.find(x => Number(x.id) === Number(id));
    return u && u.pickpriority != null ? u.pickpriority : '';
  };
  const unitPickType = (id) => {
    const u = units.find(x => Number(x.id) === Number(id));
    return u && u.picktype != null ? u.picktype : '';
  };

  const addRow = () => {
    const first = units[0]?.id || 0;
    setRows(prev => [{ id: null, whstorageunit: first, fee: 0, name: unitName(first), _new: true }, ...prev]);
  };

  const saveRow = async (row) => {
    setSavingId(row.id || 'new');
    try {
      // Persist template fee (per storage unit)
      const body = { whstorageunit: Number(row.whstorageunit), fee: Number(row.fee) };
      if (!row.id) {
        const res = await fetch(`${API}/api/templates/warehousefees`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const j = await res.json();
        if (!res.ok || j?.error) throw new Error(j?.error || 'Create failed');
        setRows(prev => prev.map(r => (r === row ? { ...j, _new: undefined } : r)));
      } else {
        const res = await fetch(`${API}/api/templates/warehousefees/${row.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const j = await res.json().catch(()=>({}));
        if (!res.ok || j?.error) throw new Error(j?.error || 'Save failed');
        setRows(prev => prev.map(r => (r.id === row.id ? { ...row, name: unitName(row.whstorageunit) } : r)));
      }

      // Also persist pickpriority and picktype for the selected storage unit, if present
      const unit = units.find(u => Number(u.id) === Number(row.whstorageunit));
      if (unit) {
        const payload = {
          pickpriority: unit.pickpriority != null ? Number(unit.pickpriority) : undefined,
          picktype: unit.picktype != null ? Number(unit.picktype) : undefined,
        };
        const hasField = Number.isFinite(payload.pickpriority) || Number.isFinite(payload.picktype);
        if (hasField) {
          try {
            const res2 = await fetch(`${API}/api/storageunits/${unit.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            const j2 = await res2.json().catch(()=>({}));
            if (!res2.ok || j2?.error) {
              console.error('Save storage unit pick fields failed', j2?.error || res2.statusText);
            }
          } catch (e) {
            console.error('Save storage unit pick fields failed', e?.message || e);
          }
        }
      }
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!row.id) { setRows(prev => prev.filter(r => r !== row)); return; }
    if (!window.confirm('Delete this template fee?')) return;
    try {
      const res = await fetch(`${API}/api/templates/warehousefees/${row.id}`, { method: 'DELETE' });
      const j = await res.json().catch(()=>({}));
      if (!res.ok || j?.error) throw new Error(j?.error || 'Delete failed');
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };

  return (
    <div className="invoices-wrapper" style={{ gap: 12, width: 'auto' }}>
      <div className="invoice-filters" style={{ justifyContent:'space-between' }}>
        <h2 style={{ margin: 0 }}>Edit Warehouse Fees</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn" onClick={addRow}><FaPlus style={{ marginRight: 6 }}/> Add</button>
          <button className="btn" onClick={async()=>{
            setLoading(true);
            try {
              const rRes = await fetch(`${API}/api/templates/warehousefees`);
              const r = await rRes.json(); setRows(Array.isArray(r) ? r : []);
            } finally { setLoading(false); }
          }}><FaSync style={{ marginRight: 6 }}/> Refresh</button>
        </div>
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <div className="invoice-table-wrapper">
          <table className="invoice-table shrink-cols">
            <thead>
              <tr>
                <th className="actions-col" title="Actions" aria-label="Actions"></th>
                <th className="fit-col">Storage Unit</th>
                <th className="fit-col">Pick Type</th>
                <th className="fit-col">Fee ($)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((r, idx) => (
                <tr key={r.id || `n-${idx}`}>
                  <td className="actions-cell">
                    <button className="mini-btn" onClick={() => saveRow(r)} disabled={savingId!=null} title="Save"><FaSave size={16}/></button>
                    <button className="mini-btn danger" onClick={() => deleteRow(r)} title="Delete"><FaTrash size={16}/></button>
                  </td>
                  <td className="fit-col">
                    <select
                      className="fit-select"
                      value={r.whstorageunit ?? 0}
                      onChange={e => setRows(prev => prev.map(x => x === r ? { ...x, whstorageunit: Number(e.target.value), name: unitName(e.target.value) } : x))}
                    >
                      {units.map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
                    </select>
                  </td>
                  <td className="fit-col">
                    <select
                      className="fit-select"
                      value={String(unitPickType(r.whstorageunit) === '' ? 0 : unitPickType(r.whstorageunit))}
                      onChange={e => {
                        const val = e.target.value;
                        setUnits(prev => prev.map(u => (
                          Number(u.id) === Number(r.whstorageunit)
                            ? { ...u, picktype: Number(val) }
                            : u
                        )));
                      }}
                    >
                      <option value="0">Standard</option>
                      <option value="1">Picking</option>
                    </select>
                  </td>
                  <td className="fit-col">
                    <input
                      type="number"
                      step="0.01"
                      value={r.fee}
                      onChange={e => setRows(prev => prev.map(x => x === r ? { ...x, fee: e.target.value } : x))}
                      style={{ width: '7ch' }}
                    />
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={4}>No template fees</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
